import { useState, useEffect } from 'react';
import { apiGet, apiPost, apiDelete } from '@/utils/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  ArrowLeft, 
  Plus,
  Search,
  Loader2,
  Trash2,
  DollarSign,
  TrendingUp,
  TrendingDown,
  ArrowUpDown,
  Calendar,
  User,
  Filter
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

// Transaction type colors
const getTransactionTypeConfig = (type) => {
  switch (type?.toUpperCase()) {
    case 'ADD':
    case 'PAYMENT':
      return {
        bg: 'bg-emerald-500/20',
        border: 'border-emerald-500/50',
        text: 'text-emerald-400',
        icon: TrendingUp,
        label: 'PAYMENT',
        sign: '+'
      };
    case 'DEDUCT':
    case 'DEDUCTION':
      return {
        bg: 'bg-red-500/20',
        border: 'border-red-500/50',
        text: 'text-red-400',
        icon: TrendingDown,
        label: 'DEDUCTION',
        sign: '-'
      };
    case 'ADJUST':
    case 'ADJUSTMENT':
      return {
        bg: 'bg-blue-500/20',
        border: 'border-blue-500/50',
        text: 'text-blue-400',
        icon: ArrowUpDown,
        label: 'ADJUSTMENT',
        sign: '±'
      };
    default:
      return {
        bg: 'bg-slate-500/20',
        border: 'border-slate-500/50',
        text: 'text-slate-400',
        icon: DollarSign,
        label: type || 'UNKNOWN',
        sign: ''
      };
  }
};

// Transaction Card Component
const TransactionCard = ({ transaction, onDelete }) => {
  const typeConfig = getTransactionTypeConfig(transaction.type);
  const TypeIcon = typeConfig.icon;
  
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-GB', { 
        day: '2-digit', 
        month: 'short', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <Card className={`bg-gradient-to-br from-[#1A2D47] to-[#0F2540] border ${typeConfig.border} rounded-xl overflow-hidden`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          {/* Left: Type & Amount */}
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl ${typeConfig.bg} flex items-center justify-center`}>
              <TypeIcon className={`h-5 w-5 ${typeConfig.text}`} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className={`text-xs font-bold ${typeConfig.text}`}>{typeConfig.label}</span>
                <span className={`text-lg font-bold ${typeConfig.text}`}>
                  {typeConfig.sign} AED {(transaction.amount || 0).toLocaleString()}
                </span>
              </div>
              <p className="text-xs text-white/50 mt-0.5">{transaction.notes || 'No notes'}</p>
            </div>
          </div>

          {/* Right: Delete */}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onDelete(transaction)}
            className="h-8 w-8 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>

        {/* Worker & Date */}
        <div className="mt-3 pt-3 border-t border-white/5 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <User className="h-3 w-3 text-white/40" />
            <span className="text-xs text-white/60">{transaction.workerName || 'Unknown Worker'}</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="h-3 w-3 text-white/40" />
            <span className="text-xs text-white/60">{formatDate(transaction.date || transaction.created_at)}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const TransactionsPage = () => {
  const navigate = useNavigate();
  
  const [transactions, setTransactions] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  
  // Add Transaction Dialog
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    case_id: '',
    type: 'ADD',
    amount: '',
    notes: '',
    date: new Date().toISOString().slice(0, 16)
  });

  // Load transactions
  const loadTransactions = async () => {
    try {
      const data = await apiGet('/api/case-transactions');
      setTransactions(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading transactions:', error);
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  // Load workers for dropdown
  const loadWorkers = async () => {
    try {
      const data = await apiGet('/api/case-transactions/workers-list');
      // API returns {workers: [...]} with name field
      const workersList = data?.workers || [];
      setWorkers(workersList.map(w => ({
        id: w.id,
        workerName: w.name || 'Unknown',
        supplierName: w.supplier || '',
        remaining: 0
      })));
    } catch (error) {
      console.error('Error loading workers:', error);
      // Fallback to cases endpoint
      try {
        const cases = await apiGet('/api/cases');
        setWorkers(Array.isArray(cases) ? cases.map(c => ({
          id: c.id,
          workerName: c.workerName,
          supplierName: c.supplierName || '',
          remaining: c.remaining || 0
        })) : []);
      } catch (e) {
        console.error('Error loading cases:', e);
      }
    }
  };

  useEffect(() => {
    loadTransactions();
    loadWorkers();
  }, []);

  // Save transaction
  const handleSave = async () => {
    if (!formData.case_id) {
      toast.error('Please select a worker');
      return;
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setSaving(true);
    try {
      await apiPost('/api/case-transactions', {
        case_id: formData.case_id,
        type: formData.type,
        amount: parseFloat(formData.amount),
        notes: formData.notes,
        date: formData.date
      });
      toast.success('Transaction added successfully!');
      setShowAddDialog(false);
      setFormData({
        case_id: '',
        type: 'ADD',
        amount: '',
        notes: '',
        date: new Date().toISOString().slice(0, 16)
      });
      loadTransactions();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to add transaction: ' + (error.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  // Delete transaction
  const handleDelete = async (transaction) => {
    if (!window.confirm('Delete this transaction?')) return;

    try {
      await apiDelete(`/api/case-transactions/${transaction.id}`);
      toast.success('Transaction deleted');
      loadTransactions();
    } catch (error) {
      toast.error('Failed to delete transaction');
    }
  };

  // Filter transactions
  const filteredTransactions = transactions.filter(t => {
    const matchesSearch = 
      (t.workerName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (t.notes || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    if (filterType === 'all') return matchesSearch;
    return matchesSearch && t.type?.toUpperCase() === filterType.toUpperCase();
  });

  // Calculate stats
  const stats = {
    total: transactions.length,
    payments: transactions.filter(t => ['ADD', 'PAYMENT'].includes(t.type?.toUpperCase())).length,
    deductions: transactions.filter(t => ['DEDUCT', 'DEDUCTION'].includes(t.type?.toUpperCase())).length,
    totalAmount: transactions.reduce((sum, t) => {
      const amount = t.amount || 0;
      if (['ADD', 'PAYMENT'].includes(t.type?.toUpperCase())) return sum + amount;
      if (['DEDUCT', 'DEDUCTION'].includes(t.type?.toUpperCase())) return sum - amount;
      return sum;
    }, 0)
  };

  return (
    <div className="space-y-6" data-testid="transactions-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/dashboard')}
            className="h-11 w-11 rounded-full bg-[#2d3c59] hover:bg-[#3e527a]"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-3xl md:text-4xl font-bold text-white italic">Transactions</h1>
        </div>
        
        <Button
          onClick={() => setShowAddDialog(true)}
          className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Transaction
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        <div className="text-center p-3 bg-[#1A2D47] rounded-xl">
          <p className="text-xl font-bold text-white">{stats.total}</p>
          <p className="text-[10px] text-white/50">Total</p>
        </div>
        <div className="text-center p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-xl">
          <p className="text-xl font-bold text-emerald-400">{stats.payments}</p>
          <p className="text-[10px] text-emerald-400/70">Payments</p>
        </div>
        <div className="text-center p-3 bg-red-500/10 border border-red-500/30 rounded-xl">
          <p className="text-xl font-bold text-red-400">{stats.deductions}</p>
          <p className="text-[10px] text-red-400/70">Deductions</p>
        </div>
        <div className="text-center p-3 bg-blue-500/10 border border-blue-500/30 rounded-xl">
          <p className="text-xl font-bold text-blue-400">
            {stats.totalAmount >= 0 ? '+' : ''}{stats.totalAmount.toLocaleString()}
          </p>
          <p className="text-[10px] text-blue-400/70">Net (AED)</p>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by worker name or notes..."
            className="pl-10 bg-[#1A2D47] border-[#004B93]/30 text-white rounded-xl"
          />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-40 bg-[#1A2D47] border-[#004B93]/30 text-white rounded-xl">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Filter" />
          </SelectTrigger>
          <SelectContent className="bg-[#1A2D47] border-[#004B93]/30">
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="ADD">Payments</SelectItem>
            <SelectItem value="DEDUCT">Deductions</SelectItem>
            <SelectItem value="ADJUST">Adjustments</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Transactions List */}
      <div className="space-y-3">
        {loading ? (
          [...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-xl bg-[#1A2D47]" />
          ))
        ) : filteredTransactions.length === 0 ? (
          <Card className="bg-[#1A2D47] border-[#004B93]/30 rounded-2xl">
            <CardContent className="p-8 text-center">
              <div className="h-16 w-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
                <DollarSign className="h-8 w-8 text-white/30" />
              </div>
              <p className="text-white/50">
                {searchTerm ? 'No transactions found' : 'No transactions yet. Add your first transaction.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredTransactions.map(transaction => (
            <TransactionCard
              key={transaction.id}
              transaction={transaction}
              onDelete={handleDelete}
            />
          ))
        )}
      </div>

      {/* Add Transaction Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-[#1A2D47] border-[#004B93]/30 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-emerald-400" />
              Add Financial Transaction
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Worker Select */}
            <div className="space-y-2">
              <Label>Worker *</Label>
              <Select 
                value={formData.case_id} 
                onValueChange={(val) => setFormData({ ...formData, case_id: val })}
              >
                <SelectTrigger className="bg-[#0F2540] border-[#004B93]/30 text-white">
                  <SelectValue placeholder="Select worker..." />
                </SelectTrigger>
                <SelectContent className="bg-[#1A2D47] border-[#004B93]/30 max-h-60">
                  {workers.map(w => (
                    <SelectItem key={w.id} value={w.id}>
                      {w.workerName} {w.supplierName && `(${w.supplierName})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Transaction Type */}
            <div className="space-y-2">
              <Label>Transaction Type *</Label>
              <div className="grid grid-cols-3 gap-2">
                <Button
                  type="button"
                  variant={formData.type === 'ADD' ? 'default' : 'outline'}
                  onClick={() => setFormData({ ...formData, type: 'ADD' })}
                  className={formData.type === 'ADD' ? 'bg-emerald-500' : 'border-emerald-500/30 text-emerald-400'}
                >
                  <TrendingUp className="h-4 w-4 mr-1" />
                  Payment
                </Button>
                <Button
                  type="button"
                  variant={formData.type === 'DEDUCT' ? 'default' : 'outline'}
                  onClick={() => setFormData({ ...formData, type: 'DEDUCT' })}
                  className={formData.type === 'DEDUCT' ? 'bg-red-500' : 'border-red-500/30 text-red-400'}
                >
                  <TrendingDown className="h-4 w-4 mr-1" />
                  Deduct
                </Button>
                <Button
                  type="button"
                  variant={formData.type === 'ADJUST' ? 'default' : 'outline'}
                  onClick={() => setFormData({ ...formData, type: 'ADJUST' })}
                  className={formData.type === 'ADJUST' ? 'bg-blue-500' : 'border-blue-500/30 text-blue-400'}
                >
                  <ArrowUpDown className="h-4 w-4 mr-1" />
                  Adjust
                </Button>
              </div>
            </div>

            {/* Amount */}
            <div className="space-y-2">
              <Label>Amount (AED) *</Label>
              <Input
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                placeholder="0.00"
                className="bg-[#0F2540] border-[#004B93]/30 text-white"
              />
            </div>

            {/* Date */}
            <div className="space-y-2">
              <Label>Transaction Date *</Label>
              <Input
                type="datetime-local"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="bg-[#0F2540] border-[#004B93]/30 text-white"
              />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Optional notes..."
                className="bg-[#0F2540] border-[#004B93]/30 text-white"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAddDialog(false)}
              className="border-white/20 text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-emerald-500 hover:bg-emerald-600"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Transaction
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TransactionsPage;
